import React, { useEffect, useRef, useState } from "react";
import { Star, ChevronLeft, ChevronRight, ArrowUpRight, Quote } from "lucide-react";
import { api_get, apiError } from "../lib/api";
import SmartImage from "./SmartImage";

const StarRow = ({ rating = 5 }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
            <Star
                key={i}
                className={`w-4 h-4 ${
                    i <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-300"
                }`}
            />
        ))}
    </div>
);

/**
 * Auto-playing reviews carousel.
 * Fetches /api/reviews → displays Google reviews if enabled, manual fallback otherwise.
 * Pauses on hover, swipeable on mobile.
 */
const ReviewsCarousel = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const trackRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api_get("/reviews");
                if (!cancelled) setData(r);
            } catch (err) {
                if (!cancelled) setError(apiError(err));
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const reviews = data?.reviews || [];
    const autoplayMs = data?.autoplayMs || 5000;

    /* Auto-advance */
    useEffect(() => {
        if (paused || reviews.length < 2) return;
        const id = setInterval(() => {
            setIdx((v) => (v + 1) % reviews.length);
        }, autoplayMs);
        return () => clearInterval(id);
    }, [paused, reviews.length, autoplayMs]);

    /* Touch / swipe */
    const touchStartX = useRef(null);
    const onTouchStart = (e) => (touchStartX.current = e.changedTouches[0].clientX);
    const onTouchEnd = (e) => {
        if (touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(dx) > 50) {
            setIdx((v) =>
                dx > 0 ? (v - 1 + reviews.length) % reviews.length : (v + 1) % reviews.length,
            );
        }
        touchStartX.current = null;
    };

    if (error) {
        return null; /* graceful — never break the page */
    }

    if (!data) {
        return (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
                <div className="text-center text-[var(--ink-soft)]">Loading reviews…</div>
            </section>
        );
    }

    if (reviews.length === 0) {
        return (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
                <div className="text-center text-[var(--ink-soft)]">
                    Google reviews are currently loading. Please check back soon.
                </div>
            </section>
        );
    }

    const goto = (i) => setIdx((i + reviews.length) % reviews.length);

    return (
        <section
            className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28"
            aria-labelledby="reviews-title"
            data-testid="reviews-carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* HEADER */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end mb-10 lg:mb-14">
                <div className="lg:col-span-7">
                    <div className="hz-pill">{data.badgeText}</div>
                    <h2
                        id="reviews-title"
                        className="font-hz-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05]"
                    >
                        {data.sectionTitle}
                    </h2>
                    <p className="text-[var(--ink-soft)] mt-3 text-base leading-relaxed max-w-xl">
                        {data.sectionSubtitle}
                    </p>
                </div>
                <div className="lg:col-span-5 lg:text-right">
                    <div className="font-hz-display text-5xl md:text-6xl tracking-tight leading-none text-[var(--brand)]">
                        {(data.rating || 4.9).toFixed(1)}
                        <span className="text-2xl text-[var(--ink-softer)] font-normal">/5</span>
                    </div>
                    <div className="mt-2 flex lg:justify-end">
                        <StarRow rating={Math.round(data.rating || 5)} />
                    </div>
                    <div className="text-xs text-[var(--ink-softer)] mt-2 uppercase tracking-widest">
                        {data.secondaryText} · {data.totalReviews}+ reviews
                    </div>
                </div>
            </div>

            {/* CAROUSEL TRACK (single review at a time, sliding) */}
            <div
                className="relative overflow-hidden rounded-[28px] bg-white border border-[var(--border)]"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                ref={trackRef}
            >
                <div
                    className="flex transition-transform duration-700 ease-out"
                    style={{ transform: `translateX(-${idx * 100}%)` }}
                >
                    {reviews.map((r, i) => (
                        <article
                            key={r.id || `${r.authorName}-${i}`}
                            className="w-full shrink-0 px-6 sm:px-10 lg:px-14 py-10 lg:py-14"
                            aria-hidden={i !== idx}
                            data-testid={`review-slide-${i}`}
                        >
                            <Quote
                                className="w-9 h-9 text-[var(--brand)] opacity-25"
                                strokeWidth={2.5}
                            />
                            <p className="font-hz-display text-xl md:text-3xl text-[var(--ink)] leading-snug mt-5 max-w-3xl">
                                “{r.text}”
                            </p>
                            <div className="mt-7 flex items-center gap-4">
                                {r.authorPhoto ? (
                                    <SmartImage
                                        src={r.authorPhoto}
                                        alt={r.authorName}
                                        className="w-12 h-12 rounded-full"
                                    />
                                ) : (
                                    <div className="w-12 h-12 rounded-full bg-[var(--brand)] text-white flex items-center justify-center font-semibold text-lg">
                                        {r.authorName?.[0]?.toUpperCase() || "C"}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-[var(--ink)]">
                                        {r.authorName}
                                    </div>
                                    <div className="text-xs text-[var(--ink-softer)] flex items-center gap-2">
                                        <span>Google Review</span>
                                        <span>·</span>
                                        <span>{r.relativeTime}</span>
                                    </div>
                                </div>
                                <StarRow rating={r.rating || 5} />
                            </div>
                        </article>
                    ))}
                </div>

                {/* Arrows */}
                {reviews.length > 1 && (
                    <>
                        <button
                            type="button"
                            onClick={() => goto(idx - 1)}
                            aria-label="Previous review"
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[var(--border)] shadow-md flex items-center justify-center hover:bg-[var(--bg-surface)] transition"
                            data-testid="review-prev"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => goto(idx + 1)}
                            aria-label="Next review"
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[var(--border)] shadow-md flex items-center justify-center hover:bg-[var(--bg-surface)] transition"
                            data-testid="review-next"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </>
                )}
            </div>

            {/* DOTS + CTA */}
            <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2" data-testid="review-dots">
                    {reviews.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => goto(i)}
                            aria-label={`Go to review ${i + 1}`}
                            className={`h-1.5 rounded-full transition-all ${
                                i === idx
                                    ? "w-8 bg-[var(--brand)]"
                                    : "w-2 bg-[var(--border)] hover:bg-[var(--ink-softer)]"
                            }`}
                            data-testid={`review-dot-${i}`}
                        />
                    ))}
                </div>
                {data.buttonUrl && (
                    <a
                        href={data.buttonUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hz-btn-ghost text-sm"
                        data-testid="reviews-cta"
                    >
                        {data.buttonText} <ArrowUpRight className="w-4 h-4" />
                    </a>
                )}
            </div>
        </section>
    );
};

export default ReviewsCarousel;
